import {
  Box,
  Heading,
  HStack,
  Select,
  Stack,
  VStack,
  SimpleGrid,
  Text,
  Image,
  Progress,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import actions from "../../actions";
import {
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
} from "recharts";
import type { TooltipProps } from "recharts";
import dayjs from "dayjs";
import {
  FaCalendar,
  FaClock,
  FaDollarSign,
  FaPercentage,
  FaTicketAlt,
} from "react-icons/fa";
import { Card as StatCard } from "../Card";
import {
  PeriodMetrics,
  RecentlyViewedEventsRecord,
  TopEventsMetric,
  DashboardSalesMetric,
} from "./types";
import {
  colorForPercentage,
  dineroFormatCurrency,
  formatNumber,
} from "../../utils";
import { Link } from "react-router-dom";

type Period =
  | "1d"
  | "7d"
  | "28d"
  | "3m"
  | "12m"
  | "last_year"
  | "mtd"
  | "qtd"
  | "ytd";

const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const formatMinorUnitCurrencyCompact = (value: number) => {
  if (!Number.isFinite(value)) {
    return "$0";
  }

  return compactCurrencyFormatter.format(value / 100);
};

const Dashboard = () => {
  const [sales, setSales] = useState<DashboardSalesMetric>(null);
  const [dashboardState, setDashboardState] = useState({
    recentlyViewedEventMetrics: null as RecentlyViewedEventsRecord | null,
    topEventsMetrics: null as TopEventsMetric | null,
    periodMetrics: null as PeriodMetrics | null,
    period: {
      timezone: "America/New_York",
      start_at: "",
      end_at: "",
    },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState<Period>("1d");

  useEffect(() => {
    try {
      setIsLoading(true);
      actions.ADMIN.fetchDashboard(period).then((response) => {
        setDashboardState(() => ({
          recentlyViewedEventMetrics: response.recently_viewed_events,
          topEventsMetrics: response.events,
          periodMetrics: response.period_metrics,
          period: response.period,
        }));
        setSales(response.sales);
      });
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      return (
        <Stack
          p={3}
          spacing={1}
          borderColor={"gray.300"}
          borderWidth={"1px"}
          rounded={"md"}
          bg="white"
        >
          <Text fontSize={"xs"} fontWeight={"bold"}>
            {label}
          </Text>
          <Divider />
          <Stack spacing={1}>
            <Text color="#4CAF50" fontSize={"sm"}>
              Sales: {dineroFormatCurrency(payload[0]?.value)}
            </Text>
          </Stack>
        </Stack>
      );
    }
    return null;
  };

  const periods = [
    { label: "Today", value: "1d" },
    { label: "Last 7 Days", value: "7d" },
    { label: "Last 4 Weeks", value: "28d" },
    { label: "Last 3 Months", value: "3m" },
    { label: "Last 12 Months", value: "12m" },
    { label: "Last Year", value: "last_year" },
    { label: "Month to Date", value: "mtd" },
    { label: "Quarter to Date", value: "qtd" },
    { label: "Year to Date", value: "ytd" },
  ];

  const selectedPeriod = periods.find((p) => p.value === period);
  const saleStartDate = dashboardState.period.start_at
    ? dayjs
        .utc(dashboardState.period.start_at, null, true)
        .tz(dashboardState.period.timezone)
    : dayjs();
  const saleEndDate = dashboardState.period.end_at
    ? dayjs
        .utc(dashboardState.period.end_at, null, true)
        .tz(dashboardState.period.timezone)
    : dayjs().tz(dashboardState.period.timezone);

  const salesAggregated = sales?.data_points?.length
    ? sales.data_points
        ?.map((v) => ({
          grossSales: v.total_sales.amount,
          timestamp: v.timestamp,
        }))
        .reverse()
    : [
        {
          grossSales: 0,
          timestamp: new Date().toISOString(),
        },
      ];

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <Box overflowY="scroll" height="100%">
      <VStack px={4} py={8} maxWidth="7xl" mx="auto" gap={8} align="stretch">
        <Stack
          alignItems="flex-start"
          width="100%"
          direction={{
            base: "column",
            sm: "column",
            md: "column",
            "2xl": "row",
          }}
          spacing={8}
          align="stretch"
        >
          <Stack flex="1" spacing={8} align="stretch" width="100%">
            <Heading fontSize="24px">Sales Overview</Heading>
            <Box>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value as Period)}
                width="216px"
                mb={2}
              >
                {periods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </Select>
              <Text color="gray.500" fontSize="sm">
                {saleStartDate.format("MMM D, YYYY")}
                {/* if the start and end date are the same, don't show the end date */}
                {saleStartDate.format("MMM D") !== saleEndDate.format("MMM D")
                  ? ` - ${saleEndDate.format("MMM D, YYYY")}`
                  : ""}
              </Text>
            </Box>
            <Stack direction={{ base: "column", lg: "row" }} spacing={4}>
              <StatCard
                width="100%"
                label="Gross Sales"
                value={sales?.display_total_sales || "$0.00"}
                icon={
                  <Box
                    bg="#c9c9c9"
                    borderRadius="40%"
                    p={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="3xl"
                  >
                    <FaDollarSign color="white" />
                  </Box>
                }
              />
              <StatCard
                width="100%"
                label="Tickets Sold"
                value={formatNumber(sales?.tickets_count || 0)}
                icon={
                  <Box
                    bg="#c9c9c9"
                    borderRadius="40%"
                    p={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="3xl"
                  >
                    <FaTicketAlt color="white" />
                  </Box>
                }
              />
              <StatCard
                width="100%"
                label="Events on Sale"
                value={
                  dashboardState.topEventsMetrics?.total_events_on_sale || 0
                }
                icon={
                  <Box
                    bg="#c9c9c9"
                    borderRadius="40%"
                    p={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="3xl"
                  >
                    <FaCalendar color="white" />
                  </Box>
                }
              />
            </Stack>
            <Stack
              rounded="lg"
              width="100%"
              height="500px"
              border="1px"
              borderColor="gray.100"
              py={2}
              px={4}
              gap={4}
            >
              <Text mb={6} fontWeight={500}>
                Sales Over Time
              </Text>

              <Text fontWeight={700}>Activity</Text>
              <ResponsiveContainer>
                <BarChart style={{ padding: "10px" }} data={salesAggregated}>
                  <Tooltip
                    cursor={{ fill: "#eee" }}
                    content={<CustomTooltip />}
                  />
                  {/* <Line
                    type="monotone"
                    dataKey="eventsOnSale"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={false}
                    name="Events on Sale"
                  /> */}
                  <Bar
                    type="monotone"
                    dataKey="grossSales"
                    fill="#4CAF50"
                    strokeWidth={2}
                    name="Sales"
                  />
                  {/* <Line
                    type="monotone"
                    dataKey="ticketsSold"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={false}
                    name="Tickets Sold"
                  /> */}
                  <XAxis
                    dataKey="timestamp"
                    interval="preserveStartEnd"
                    //tickFormatter={(value) => dayjs(value).format("MMM D")}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickFormatter={formatMinorUnitCurrencyCompact}
                  />
                  {/* <Legend /> */}
                </BarChart>
              </ResponsiveContainer>
            </Stack>
          </Stack>

          <Box width={{ base: "100%", "2xl": "393px" }}>
            <Heading fontSize="24px" mb={8}>
              Top Events {selectedPeriod?.label}
            </Heading>
            {dashboardState.topEventsMetrics?.events?.length === 0 ? (
              <Text>No sales {selectedPeriod?.label.toLowerCase()}</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, lg: 1, "2xl": 1 }} spacing={4}>
                {dashboardState.topEventsMetrics?.events.map((event) => (
                  <Link to={`/events/${event.event_id}`}>
                    <Box
                      px={{ base: "4" }}
                      py={{ base: "2" }}
                      bg="bg-surface"
                      borderRadius="lg"
                      borderWidth={"1px"}
                      borderColor="gray.200"
                      key={event.event_id}
                    >
                      <HStack spacing={4} align="center">
                        <Image
                          rounded="lg"
                          src={event.summary.image}
                          boxShadow="0px 4px 10px 0px rgba(0, 0, 0, 0.25)"
                          alt={event.event_name}
                          width="100px"
                          height="100px"
                          objectFit="cover"
                          fallbackSrc="https://via.placeholder.com/100"
                        />
                        <VStack align="stretch" flex={1} spacing={1}>
                          <Text
                            fontWeight="medium"
                            color={"#4C4C4C"}
                            noOfLines={2}
                          >
                            {event.event_name}
                          </Text>
                          <HStack spacing={2}>
                            <Box color="#00B69B">
                              <FaPercentage />
                            </Box>
                            <Text fontSize="sm">
                              <Text as="span" color="#00B69B" fontWeight="bold">
                                {Math.round(event.percentage_of_total * 100)}%
                              </Text>
                              <Text as="span" color="#4C4C4C">
                                {" "}
                                of sales
                              </Text>
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Box color="#00B69B">
                              <FaDollarSign />
                            </Box>
                            <Text fontSize="sm">
                              <Text as="span" color="#00B69B" fontWeight="bold">
                                {event.display_sales}
                              </Text>
                              <Text as="span" color="#4C4C4C">
                                {" "}
                                {selectedPeriod?.label.toLowerCase()}
                              </Text>
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Box color="#00B69B">
                              <FaTicketAlt />
                            </Box>
                            <Text fontSize="sm">
                              <Text as="span" color="#00B69B" fontWeight="bold">
                                {formatNumber(event.tickets_sold)}
                              </Text>
                              <Text as="span" color="#4C4C4C">
                                {" "}
                                tickets {selectedPeriod?.label.toLowerCase()}
                              </Text>
                            </Text>
                          </HStack>
                        </VStack>
                      </HStack>
                    </Box>
                  </Link>
                ))}
              </SimpleGrid>
            )}
          </Box>
        </Stack>
        <Heading fontSize="24px">Recently Viewed Events</Heading>
        {!dashboardState.recentlyViewedEventMetrics?.events ||
        dashboardState.recentlyViewedEventMetrics?.events?.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            No recently viewed events
          </Text>
        ) : (
          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing={14}
            width="full"
            justifyItems={{ base: "center" }}
          >
            {dashboardState.recentlyViewedEventMetrics?.events?.map((event) => {
              const percentage =
                (event.summary.realtime_metrics?.tickets /
                  event.summary.realtime_metrics?.quantity) *
                100;
              return (
                <Link to={`/events/${event.event_id}`}>
                  {" "}
                  <VStack spacing={1} align={{ base: "center", sm: "stretch" }}>
                    <Image
                      rounded="lg"
                      src={event.summary.image}
                      alt={event.summary.name}
                      width="100%"
                      aspectRatio="1/1"
                      objectFit="cover"
                      borderRadius="md"
                      boxShadow="0px 4px 10px 0px rgba(0, 0, 0, 0.25)"
                    />
                    <Text noOfLines={2}>{event.summary.name}</Text>
                    <Stack direction="row" justify="space-between">
                      <Text fontSize="sm" color="gray.600">
                        {dayjs(event.summary.start_at).format(
                          "dddd, MMMM D, YYYY"
                        )}
                      </Text>
                      {event.summary.total_waitlist_buy_orders > 0 && (
                        <HStack>
                          <FaClock />
                          <Text fontSize="sm" color="gray.600">
                            {event.summary.total_waitlist_buy_orders_display}
                          </Text>
                        </HStack>
                      )}
                    </Stack>

                    <Box width="full">
                      <Progress
                        value={percentage}
                        size="sm"
                        colorScheme={colorForPercentage(percentage)}
                        borderRadius={12}
                      />
                    </Box>

                    <HStack width="full" justify="space-between">
                      <Text fontSize="sm">
                        {event.summary.realtime_metrics?.tickets} /{" "}
                        {event.summary.realtime_metrics?.quantity}
                      </Text>
                      <Text fontSize="sm">
                        {event.summary.realtime_metrics?.sales_display}
                      </Text>
                    </HStack>
                  </VStack>
                </Link>
              );
            })}
          </SimpleGrid>
        )}
      </VStack>
    </Box>
  );
};

export default Dashboard;
